/**
 * Integration Code Viewer Component (Step 8)
 *
 * Displays and manages generated integration code packages:
 * - ETL Scripts (Python): Extract-Transform-Load scripts
 * - Cypher Queries: Node/relationship upsert queries
 * - Orchestration: Airflow DAGs for scheduling
 * - Configuration: Docker, Debezium, Kafka configs
 * - Monitoring: Health checks, metrics collectors
 * - Documentation: README, setup instructions
 *
 * Features:
 * - Syntax-highlighted code viewer
 * - File tree navigation
 * - Download individual files or full package
 * - Code validation with error display
 * - Strategic question traceability
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { vs2015, docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {
  Code,
  FileCode,
  FolderOpen,
  Folder,
  Download,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Copy,
  Check,
  Play,
  FileText,
  Settings,
  Database,
  Terminal,
  Package,
  Zap,
  Eye,
  ChevronRight,
  ChevronDown,
  Search,
  Filter,
} from 'lucide-react';
import { Card, CardBody as CardContent, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Spinner from '../ui/Spinner';
import {
  generateIntegrationPackage,
  getIntegrationPackage,
  getPackagesForCase,
  getArtifact,
  downloadArtifact,
  downloadPackage,
  getArtifactsByCategory,
  validatePackage,
} from '../../lib/api/designPhaseApi';
import type {
  IntegrationPackage,
  CodeArtifact,
  PackageSummary,
  GenerationStatus,
  ArtifactType,
  ArtifactCategory,
} from '../../types/designPhase';

interface IntegrationCodeViewerProps {
  caseId: string;
  packageId?: string;
  onPackageGenerated?: (pkg: IntegrationPackage) => void;
}

// Category icons
const CATEGORY_ICONS: Record<ArtifactCategory, React.FC<{ className?: string }>> = {
  etl_extract: Database,
  etl_transform: Settings,
  etl_load: Database,
  cypher_upsert: Code,
  cypher_relationship: Code,
  orchestration: Play,
  configuration: Settings,
  cdc_connector: Zap,
  monitoring: Eye,
  documentation: FileText,
  infrastructure: Package,
};

// Category colors
const CATEGORY_COLORS: Record<ArtifactCategory, string> = {
  etl_extract: 'bg-blue-100 text-blue-700',
  etl_transform: 'bg-purple-100 text-purple-700',
  etl_load: 'bg-cyan-100 text-cyan-700',
  cypher_upsert: 'bg-green-100 text-green-700',
  cypher_relationship: 'bg-emerald-100 text-emerald-700',
  orchestration: 'bg-orange-100 text-orange-700',
  configuration: 'bg-slate-100 text-slate-700',
  cdc_connector: 'bg-yellow-100 text-yellow-700',
  monitoring: 'bg-rose-100 text-rose-700',
  documentation: 'bg-indigo-100 text-indigo-700',
  infrastructure: 'bg-amber-100 text-amber-700',
};

// Artifact type to language mapping for syntax highlighting
const TYPE_TO_LANGUAGE: Record<ArtifactType, string> = {
  python: 'python',
  cypher: 'sql', // Cypher is similar to SQL for highlighting
  yaml: 'yaml',
  json: 'json',
  dockerfile: 'dockerfile',
  markdown: 'markdown',
  sql: 'sql',
  shell: 'bash',
};

// Status colors
const STATUS_COLORS: Record<GenerationStatus, string> = {
  pending: 'bg-neutral-100 text-neutral-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

// Group artifacts by category
const groupArtifactsByCategory = (
  artifacts: CodeArtifact[]
): Map<ArtifactCategory, CodeArtifact[]> => {
  const groups = new Map<ArtifactCategory, CodeArtifact[]>();
  for (const artifact of artifacts) {
    const existing = groups.get(artifact.category) || [];
    groups.set(artifact.category, [...existing, artifact]);
  }
  return groups;
};

export const IntegrationCodeViewer: React.FC<IntegrationCodeViewerProps> = ({
  caseId,
  packageId: initialPackageId,
  onPackageGenerated,
}) => {
  const queryClient = useQueryClient();
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(initialPackageId || null);
  const [selectedArtifact, setSelectedArtifact] = useState<CodeArtifact | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<ArtifactCategory>>(
    new Set(['etl_extract', 'cypher_upsert'])
  );
  const [filterCategory, setFilterCategory] = useState<ArtifactCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    total_errors: number;
    total_warnings: number;
  } | null>(null);

  // Fetch packages for case
  const { data: packages, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages', caseId],
    queryFn: () => getPackagesForCase(caseId),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch selected package
  const {
    data: currentPackage,
    isLoading: packageLoading,
    error: packageError,
  } = useQuery({
    queryKey: ['package', selectedPackageId],
    queryFn: () => getIntegrationPackage(selectedPackageId!),
    staleTime: 5 * 60 * 1000,
    enabled: !!selectedPackageId,
  });

  // Generate package mutation
  const generateMutation = useMutation({
    mutationFn: () =>
      generateIntegrationPackage(caseId, {
        include_etl: true,
        include_cypher: true,
        include_orchestration: true,
        include_docker: true,
        include_monitoring: true,
        include_documentation: true,
      }),
    onSuccess: (response) => {
      if (response.package) {
        queryClient.invalidateQueries({ queryKey: ['packages', caseId] });
        setSelectedPackageId(response.package.package_id);
        onPackageGenerated?.(response.package);
      }
      setIsGenerating(false);
    },
    onError: () => {
      setIsGenerating(false);
    },
  });

  // Validate package mutation
  const validateMutation = useMutation({
    mutationFn: () => validatePackage(selectedPackageId!),
    onSuccess: (result) => {
      setValidationResult(result);
      setIsValidating(false);
    },
    onError: () => {
      setIsValidating(false);
    },
  });

  // Filtered and grouped artifacts
  const groupedArtifacts = useMemo(() => {
    if (!currentPackage?.artifacts) return new Map();

    let filtered = currentPackage.artifacts;

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter((a) => a.category === filterCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.filename.toLowerCase().includes(query) ||
          a.description.toLowerCase().includes(query) ||
          a.entity_label?.toLowerCase().includes(query)
      );
    }

    return groupArtifactsByCategory(filtered);
  }, [currentPackage, filterCategory, searchQuery]);

  // Handle generate
  const handleGenerate = useCallback(() => {
    setIsGenerating(true);
    generateMutation.mutate();
  }, [generateMutation]);

  // Handle validate
  const handleValidate = useCallback(() => {
    if (!selectedPackageId) return;
    setIsValidating(true);
    validateMutation.mutate();
  }, [selectedPackageId, validateMutation]);

  // Handle copy code
  const handleCopyCode = useCallback(async () => {
    if (!selectedArtifact?.content) return;
    try {
      await navigator.clipboard.writeText(selectedArtifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [selectedArtifact]);

  // Handle download artifact
  const handleDownloadArtifact = useCallback(async () => {
    if (!selectedPackageId || !selectedArtifact) return;
    try {
      const data = await downloadArtifact(selectedPackageId, selectedArtifact.artifact_id);
      const blob = new Blob([data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
    }
  }, [selectedPackageId, selectedArtifact]);

  // Handle download full package
  const handleDownloadPackage = useCallback(async () => {
    if (!selectedPackageId) return;
    try {
      const blob = await downloadPackage(selectedPackageId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `integration-package-${selectedPackageId}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download package:', err);
    }
  }, [selectedPackageId]);

  // Toggle category expansion
  const toggleCategory = useCallback((category: ArtifactCategory) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  }, []);

  // Render category badge
  const renderCategoryBadge = (category: ArtifactCategory) => {
    const Icon = CATEGORY_ICONS[category];
    return (
      <Badge className={`flex items-center gap-1 ${CATEGORY_COLORS[category]}`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize">{category.replace('_', ' ')}</span>
      </Badge>
    );
  };

  // Loading state
  if (packagesLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Spinner className="w-8 h-8 text-primary-500 mx-auto" />
          <p className="mt-2 text-neutral-600">Loading code packages...</p>
        </div>
      </div>
    );
  }

  // No packages - show generate button
  if (!packages || packages.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Code className="w-16 h-16 text-neutral-300 mx-auto" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-700">
              No Integration Code Generated
            </h3>
            <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
              Generate integration code to create ETL scripts, Cypher queries, and deployment
              configurations for your knowledge graph.
            </p>
            <Button
              onClick={handleGenerate}
              className="mt-6"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Spinner className="w-4 h-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <Terminal className="w-4 h-4 mr-2" />
                  Generate Integration Code
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no package selected, select the first one
  if (!selectedPackageId && packages.length > 0) {
    setSelectedPackageId(packages[0].package_id);
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-xl font-bold text-neutral-900 flex items-center gap-2">
                <Code className="w-6 h-6 text-indigo-600" />
                Integration Code Package
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                {currentPackage?.total_files || 0} files,{' '}
                {currentPackage?.total_lines?.toLocaleString() || 0} lines of code
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Package selector */}
              {packages.length > 1 && (
                <select
                  className="px-3 py-1.5 rounded border border-neutral-300 text-sm"
                  value={selectedPackageId || ''}
                  onChange={(e) => setSelectedPackageId(e.target.value)}
                >
                  {packages.map((pkg) => (
                    <option key={pkg.package_id} value={pkg.package_id}>
                      {pkg.package_id} ({pkg.total_files} files)
                    </option>
                  ))}
                </select>
              )}

              {/* Status badge */}
              {currentPackage && (
                <Badge className={STATUS_COLORS[currentPackage.status]}>
                  {currentPackage.status === 'completed' ? (
                    <CheckCircle className="w-3 h-3 mr-1" />
                  ) : currentPackage.status === 'failed' ? (
                    <XCircle className="w-3 h-3 mr-1" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                  )}
                  <span className="capitalize">{currentPackage.status}</span>
                </Badge>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleValidate}
                  disabled={isValidating || !selectedPackageId}
                >
                  {isValidating ? (
                    <Spinner className="w-4 h-4" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  )}
                  Validate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPackage}
                  disabled={!selectedPackageId}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download ZIP
                </Button>
                <Button
                  size="sm"
                  onClick={handleGenerate}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <Spinner className="w-4 h-4 mr-1" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Regenerate
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation result */}
      {validationResult && (
        <Card
          className={
            validationResult.valid
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50'
          }
        >
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              {validationResult.valid ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-red-600" />
              )}
              <div>
                <p className={`font-medium ${validationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                  {validationResult.valid ? 'All code validated successfully' : 'Validation found issues'}
                </p>
                <p className={`text-sm ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                  {validationResult.total_errors} error(s), {validationResult.total_warnings} warning(s)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content - file tree + code viewer */}
      <div className="grid grid-cols-12 gap-4">
        {/* File tree sidebar */}
        <div className="col-span-4">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Files
                </h3>
                <Badge variant="outline">{currentPackage?.total_files || 0}</Badge>
              </div>
              {/* Search and filter */}
              <div className="mt-3 space-y-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search files..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border rounded"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <select
                  className="w-full px-2 py-1.5 text-sm border rounded"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as ArtifactCategory | 'all')}
                >
                  <option value="all">All categories</option>
                  {Object.keys(CATEGORY_ICONS).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto">
              <div className="space-y-2">
                {Array.from(groupedArtifacts.entries()).map(([category, artifacts]) => (
                  <div key={category} className="border rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between p-2 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                      onClick={() => toggleCategory(category)}
                    >
                      <div className="flex items-center gap-2">
                        {expandedCategories.has(category) ? (
                          <ChevronDown className="w-4 h-4 text-neutral-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-neutral-400" />
                        )}
                        {renderCategoryBadge(category)}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {artifacts.length}
                      </Badge>
                    </div>
                    {expandedCategories.has(category) && (
                      <div className="p-2 space-y-1">
                        {artifacts.map((artifact: CodeArtifact) => (
                          <div
                            key={artifact.artifact_id}
                            className={`p-2 rounded cursor-pointer text-sm transition-colors ${
                              selectedArtifact?.artifact_id === artifact.artifact_id
                                ? 'bg-primary-100 border-primary-300 border'
                                : 'hover:bg-neutral-100'
                            }`}
                            onClick={() => setSelectedArtifact(artifact)}
                          >
                            <div className="flex items-center gap-2">
                              <FileCode className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                              <span className="truncate font-mono text-xs">
                                {artifact.filename}
                              </span>
                            </div>
                            {artifact.entity_label && (
                              <p className="text-xs text-neutral-500 ml-6 truncate">
                                {artifact.entity_label}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {groupedArtifacts.size === 0 && (
                  <div className="text-center py-8 text-neutral-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No files found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Code viewer */}
        <div className="col-span-8">
          <Card className="h-full">
            {selectedArtifact ? (
              <>
                <CardHeader className="pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 font-mono">
                        <FileCode className="w-4 h-4" />
                        {selectedArtifact.filename}
                      </h3>
                      <p className="text-sm text-neutral-500 mt-1">
                        {selectedArtifact.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {renderCategoryBadge(selectedArtifact.category)}
                      <Badge variant="outline">{selectedArtifact.line_count} lines</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyCode}
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadArtifact}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Questions served */}
                  {selectedArtifact.questions_served.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-neutral-500">Serves:</span>
                      {selectedArtifact.questions_served.slice(0, 3).map((qId) => (
                        <Badge key={qId} variant="outline" className="text-xs bg-green-50 text-green-700">
                          {qId}
                        </Badge>
                      ))}
                      {selectedArtifact.questions_served.length > 3 && (
                        <span className="text-xs text-neutral-500">
                          +{selectedArtifact.questions_served.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[600px] overflow-auto">
                    <SyntaxHighlighter
                      language={TYPE_TO_LANGUAGE[selectedArtifact.artifact_type] || 'text'}
                      style={vs2015}
                      showLineNumbers
                      customStyle={{
                        margin: 0,
                        padding: '1rem',
                        fontSize: '0.8125rem',
                        lineHeight: '1.5',
                        background: '#1e1e1e',
                      }}
                    >
                      {selectedArtifact.content}
                    </SyntaxHighlighter>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="py-16">
                <div className="text-center text-neutral-500">
                  <FileCode className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a file to view its contents</p>
                  <p className="text-sm mt-2">
                    Choose a file from the tree on the left to see the generated code
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Package summary */}
      {currentPackage && (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Package Summary
            </h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-700">
                  {currentPackage.structure.etl_scripts.length}
                </div>
                <div className="text-xs text-blue-600">ETL Scripts</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">
                  {currentPackage.structure.cypher_queries.length}
                </div>
                <div className="text-xs text-green-600">Cypher Queries</div>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-700">
                  {currentPackage.structure.orchestration.length}
                </div>
                <div className="text-xs text-orange-600">DAGs</div>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-700">
                  {currentPackage.structure.configuration.length}
                </div>
                <div className="text-xs text-slate-600">Config Files</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-700">
                  {currentPackage.entities_covered.length}
                </div>
                <div className="text-xs text-purple-600">Entities</div>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-700">
                  {currentPackage.questions_covered.length}
                </div>
                <div className="text-xs text-emerald-600">Questions</div>
              </div>
            </div>

            {/* Entities and questions covered */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-2">Entities Covered:</p>
                <div className="flex flex-wrap gap-1">
                  {currentPackage.entities_covered.slice(0, 10).map((entity) => (
                    <Badge key={entity} variant="outline" className="text-xs">
                      {entity}
                    </Badge>
                  ))}
                  {currentPackage.entities_covered.length > 10 && (
                    <Badge variant="outline" className="text-xs">
                      +{currentPackage.entities_covered.length - 10} more
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-2">Questions Addressed:</p>
                <div className="flex flex-wrap gap-1">
                  {currentPackage.questions_covered.slice(0, 6).map((qId) => (
                    <Badge key={qId} variant="outline" className="text-xs bg-green-50 text-green-700">
                      {qId}
                    </Badge>
                  ))}
                  {currentPackage.questions_covered.length > 6 && (
                    <Badge variant="outline" className="text-xs">
                      +{currentPackage.questions_covered.length - 6} more
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IntegrationCodeViewer;
